import {
    Briefcase, Heart, UserCheck, Calculator, BookOpen,
    Banknote, Percent, PieChart, MapPin, Gift, FolderOpen, BarChart3, FileSpreadsheet
} from 'lucide-react';

const SOURCE_LABELS = {
    company: 'Company',
    charity: 'Charity',
    individual: 'Individual',
    unregistered: 'Unregistered Charity / Association',
    partnership: 'Partnership',
};

const SOURCE_NUMBER_LABELS = {
    company: 'Company Number',
    charity: 'Charity Number',
    individual: 'UTRN',
    unregistered: 'Entity Ref',
    partnership: 'Partnership Ref',
};

const SOURCE_TABLES = {
    company: 'clients_coy',
    charity: 'clients_charity',
    individual: 'clients_individual',
    unregistered: 'clients_unregistered',
    partnership: 'clients_partnership',
};

const BRIGHT_MANAGER_LINKS = [
    { key: 'dashboard', label: 'Main Dashboard', suffix: '' },
    { key: 'documents', label: 'Documents', suffix: '' },
    {
        key: 'documents_cosec',
        label: 'CoSec Documents',
        suffix: '/MDAgLSBDb21wYW55IFNlY3JldGFyaWFs',
        requiredAnyServiceFields: ['svc_confirmation_statement'],
    },
    {
        key: 'documents_accounts',
        label: 'Accounts Documents',
        suffix: '/MDEgLSBBY2NvdW50cw==',
        requiredAnyServiceFields: ['svc_accounts_prep'],
    },
    { key: 'documents_hmrc_main', label: 'HMRC Docs (Main)', suffix: '/MDIgLSBITVJD' },
    {
        key: 'documents_hmrc_cotax',
        label: 'HMRC Docs (CoTax)',
        suffix: '/MDIgLSBITVJDL0NvcnBvcmF0aW9uIFRheA==',
        requiredAnyServiceFields: ['svc_ct600'],
    },
    {
        key: 'documents_hmrc_cotax_correspondence',
        label: 'HMRC Docs (CoTax Correspondence)',
        suffix: '/MDIgLSBITVJDL0NvcnBvcmF0aW9uIFRheC9Db3JyZXNwb25kZW5jZQ==',
        requiredAnyServiceFields: ['svc_ct600'],
    },
    {
        key: 'documents_hmrc_ct600',
        label: 'HMRC Docs (CT600)',
        suffix: '/MDIgLSBITVJDL0NvcnBvcmF0aW9uIFRheC9DVDYwMA==',
        requiredAnyServiceFields: ['svc_ct600'],
    },
    {
        key: 'documents_hmrc_vat',
        label: 'HMRC Docs (VAT)',
        suffix: '/MDIgLSBITVJDL1ZBVA==',
        requiredAnyServiceFields: ['svc_vat_returns'],
    },
    {
        key: 'documents_hmrc_vat_correspondence',
        label: 'HMRC Docs (VAT Correspondence)',
        suffix: '/MDIgLSBITVJDL1ZBVC9Db3JyZXNwb2RlbmNl',
        requiredAnyServiceFields: ['svc_vat_returns'],
    },
    {
        key: 'documents_hmrc_vat_returns',
        label: 'HMRC Docs (VAT Returns)',
        suffix: '/MDIgLSBITVJDL1ZBVC9WQVQgUmV0dXJucw==',
        requiredAnyServiceFields: ['svc_vat_returns'],
    },
    {
        key: 'documents_hmrc_pers_tax',
        label: 'HMRC Docs (PersTax)',
        suffix: '/MDIgLSBITVJDL1BlcnNvbmFsIFRheA==',
        requiredAnyServiceFields: ['svc_self_assessment'],
    },
    {
        key: 'documents_hmrc_pers_tax_correspondence',
        label: 'HMRC Docs (PersTax Correspondence)',
        suffix: '/MDIgLSBITVJDL1BlcnNvbmFsIFRheC9Db3JyZXNwb25kZW5jZQ==',
        requiredAnyServiceFields: ['svc_self_assessment'],
    },
    {
        key: 'documents_hmrc_sa100',
        label: 'HMRC Docs (SA100)',
        suffix: '/MDIgLSBITVJDL1BlcnNvbmFsIFRheC9TQTEwMA==',
        requiredAnyServiceFields: ['svc_self_assessment'],
    },
    {
        key: 'documents_payroll_main',
        label: 'Payroll Docs (Main)',
        suffix: '/MDMgLSBQYXlyb2xs',
        requiredAnyServiceFields: ['svc_payroll'],
    },
    {
        key: 'documents_payroll_correspondence',
        label: 'Payroll Docs (Correspondence)',
        suffix: '/MDMgLSBQYXlyb2xsL0NvcnJlc3BvbmRlbmNl',
        requiredAnyServiceFields: ['svc_payroll'],
    },
    {
        key: 'documents_payroll_reports',
        label: 'Payroll Docs (Reports)',
        suffix: '/MDMgLSBQYXlyb2xsL1JlcG9ydHM=',
        requiredAnyServiceFields: ['svc_payroll'],
    },
];

const createBrightManagerSection = () => ({
    providerLabel: 'Bright Manager',
    sectionLabel: 'Bright Manager Links',
    referenceField: 'bm_urn',
    referenceLabel: 'BM URN',
    referencePlaceholder: 'e.g. 4f7f7a68-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
    referenceHelp: 'Store the Bright Manager unique client reference once and use these shortcuts for dashboard and document folders.',
    emptyState: 'Add the BM URN to unlock Bright Manager links for this client.',
    editableReference: true,
    baseUrl: 'https://manager.brightsg.com/client/',
    links: BRIGHT_MANAGER_LINKS.map((link) => ({
        ...link,
        baseUrl: link.key === 'dashboard' ? 'https://manager.brightsg.com/client/' : 'https://manager.brightsg.com/client-docs/',
    })),
});

const SHAREPOINT_CLIENT_FOLDER_ROOT = 'ms-explorer:%USERPROFILE%\\SMG Business Accountants Ltd\\SMGBA Operations - Documents\\Clients\\{ClientLetter}\\{ClientName}';

const createClientFolderTemplate = (suffix = '') => `${SHAREPOINT_CLIENT_FOLDER_ROOT}${suffix}`;

const SHAREPOINT_CLIENT_FOLDER_LINKS = [
    { key: 'sp_main', label: 'Main', urlTemplate: createClientFolderTemplate(''), encodeTemplateTokens: false },
    { key: 'sp_notes', label: 'Notes', urlTemplate: createClientFolderTemplate('\\000-Notes'), encodeTemplateTokens: false },
    { key: 'sp_management', label: 'Management', urlTemplate: createClientFolderTemplate('\\001-Management'), encodeTemplateTokens: false },
    { key: 'sp_cosec', label: 'CoSec', urlTemplate: createClientFolderTemplate('\\001-Management\\Company Secretarial'), encodeTemplateTokens: false, requiredModuleIds: ['ch_company'] },
    { key: 'sp_cosec_register', label: 'CoSec (Register)', urlTemplate: createClientFolderTemplate('\\001-Management\\Company Secretarial\\002-Register'), encodeTemplateTokens: false, requiredModuleIds: ['ch_company'] },
    { key: 'sp_general', label: 'General', urlTemplate: createClientFolderTemplate('\\002-General'), encodeTemplateTokens: false },
    { key: 'sp_accounts', label: 'Accounts', urlTemplate: createClientFolderTemplate('\\002-General\\Accounts'), encodeTemplateTokens: false },
    { key: 'sp_accounts_reports', label: 'Accounts (Reports)', urlTemplate: createClientFolderTemplate('\\002-General\\Accounts\\01-Reports'), encodeTemplateTokens: false },
    { key: 'sp_accounts_reports_annual', label: 'Accounts (Reports - Annual)', urlTemplate: createClientFolderTemplate('\\002-General\\Accounts\\01-Reports\\01-Annual Accounts'), encodeTemplateTokens: false },
    { key: 'sp_accounts_bank', label: 'Accounts (Bank Statements)', urlTemplate: createClientFolderTemplate('\\002-General\\Accounts\\Bank Statements'), encodeTemplateTokens: false },
    { key: 'sp_hmrc', label: 'HMRC', urlTemplate: createClientFolderTemplate('\\002-General\\HMRC'), encodeTemplateTokens: false },
    { key: 'sp_cotax', label: 'CoTax', urlTemplate: createClientFolderTemplate('\\002-General\\HMRC\\Corpoartion Tax'), encodeTemplateTokens: false, requiredAnyServiceFields: ['svc_ct600'] },
    { key: 'sp_cotax_ct600', label: 'CoTax (CT600)', urlTemplate: createClientFolderTemplate('\\002-General\\HMRC\\Corpoartion Tax\\CT 600'), encodeTemplateTokens: false, requiredAnyServiceFields: ['svc_ct600'] },
    { key: 'sp_vat', label: 'VAT', urlTemplate: createClientFolderTemplate('\\002-General\\HMRC\\VAT'), encodeTemplateTokens: false, requiredAnyServiceFields: ['svc_vat_returns'] },
    { key: 'sp_vat_returns', label: 'VAT (Returns)', urlTemplate: createClientFolderTemplate('\\002-General\\HMRC\\VAT\\Returns'), encodeTemplateTokens: false, requiredAnyServiceFields: ['svc_vat_returns'] },
    { key: 'sp_vat_returns_vat100', label: 'VAT (Returns - VAT100)', urlTemplate: createClientFolderTemplate('\\002-General\\HMRC\\VAT\\Returns\\VAT 100'), encodeTemplateTokens: false, requiredAnyServiceFields: ['svc_vat_returns'] },
    { key: 'sp_payroll', label: 'Payroll', urlTemplate: createClientFolderTemplate('\\002-General\\Payroll'), encodeTemplateTokens: false, requiredAnyServiceFields: ['svc_payroll'] },
    { key: 'sp_payroll_reports', label: 'Payroll (Reports)', urlTemplate: createClientFolderTemplate('\\002-General\\Payroll\\Reports'), encodeTemplateTokens: false, requiredAnyServiceFields: ['svc_payroll'] },
    { key: 'sp_charity_commission', label: 'Charity Commission', urlTemplate: createClientFolderTemplate('\\002-General\\Charities Commission'), encodeTemplateTokens: false, requiredModuleIds: ['charity'] },
    { key: 'sp_charity_commission_returns', label: 'Charity Commission (Returns)', urlTemplate: createClientFolderTemplate('\\002-General\\Charities Commission\\Returns'), encodeTemplateTokens: false, requiredModuleIds: ['charity'] },
    { key: 'sp_pension_regulator', label: 'Pension Regulator', urlTemplate: createClientFolderTemplate('\\002-General\\Pension Regulator'), encodeTemplateTokens: false, requiredAnyServiceFields: ['svc_payroll'] },
    { key: 'sp_pension_regulator_returns', label: 'Pension Regulator (Returns)', urlTemplate: createClientFolderTemplate('\\002-General\\Pension Regulator\\Returns'), encodeTemplateTokens: false, requiredAnyServiceFields: ['svc_payroll'] },
    { key: 'sp_personal_tax', label: 'Personal Tax', urlTemplate: createClientFolderTemplate('\\002-General\\HMRC\\Personal Tax'), encodeTemplateTokens: false, requiredModuleIds: ['individual'] },
    { key: 'sp_personal_tax_sa100', label: 'Personal Tax (SA100)', urlTemplate: createClientFolderTemplate('\\002-General\\HMRC\\Personal Tax\\SA 100'), encodeTemplateTokens: false, requiredModuleIds: ['individual'] },
];

const createClientFoldersSection = () => ({
    providerLabel: 'Client Folders',
    sectionLabel: 'Client Folders',
    referenceField: 'sp_client_folder',
    referenceLabel: 'Folder Key ({ClientLetter}\\{ClientName})',
    referencePlaceholder: 'e.g. A\\Accessible Care Services',
    referenceHelp: 'Enter the exact synced folder key as {ClientLetter}\\{ClientName}. This handles naming differences from StaffHub display names.',
    emptyState: 'Add the folder key (e.g. A\\Accessible Care Services) to enable SharePoint folder shortcuts.',
    editableReference: true,
    referenceFormat: 'client-folder-pair',
    baseUrl: '',
    links: SHAREPOINT_CLIENT_FOLDER_LINKS,
});

const createRelationalServiceModule = ({
    id,
    label,
    icon,
    colorClass,
    tableName,
    fetchTable,
    enrollServiceField,
    sourceTypes,
    workflowTable,
    workflowKey = 'service',
    workflowLabel = label,
    shortLabel = 'SERVICE',
    dueField = 'service_due_date',
    dueLabel = 'Due Date',
    planField = 'p_month_service',
    statusField = 'status_service',
    staffField = 'assigned_staff_service_id',
    textColorClass = 'text-slate-700',
    filterColorClass = 'bg-slate-50 border-slate-200 text-slate-700',
    dotColorClass = 'bg-slate-400',
    kpiConfig = {},
}) => {
    const singleSourceType = sourceTypes.length === 1;
    const numberLabel = singleSourceType ? SOURCE_NUMBER_LABELS[sourceTypes[0]] : 'Client Ref';
    const addTargetLabel = singleSourceType ? SOURCE_LABELS[sourceTypes[0]] : 'Client';

    return {
        id,
        label,
        icon,
        colorClass,
        tableName,
        fetchTable,
        enrollMode: true,
        enrollServiceField,
        enrollTargetTable: tableName,
        enrollSourceTypes: sourceTypes,
        realtimeTables: [tableName, ...sourceTypes.map(type => SOURCE_TABLES[type])],
        readonlyFields: [
            'client_name', 'client_ref', 'source_type', 'source_id',
            'pc_pname', 'pc_fname', 'pc_email', 'pc_tel',
        ],
        entity: {
            singular: `${label} Client`,
            plural: `${label} Clients`,
            totalLabel: `Total ${label} Clients`,
            addLabel: `Enrol ${addTargetLabel}`,
            numberLabel,
            syncLabel: ''
        },
        actions: {
            canAdd: true,
            canSync: false
        },
        kpiConfig,
        workstreams: [
            {
                key: workflowKey,
                label: workflowLabel,
                shortLabel,
                dueField,
                planField,
                statusField,
                staffField,
                workflowTable,
                refField: dueField,
                nextField: dueField,
                textColorClass,
                filterColorClass,
                dotColorClass,
            }
        ],
        workflowTables: {
            [workflowKey]: workflowTable,
        },
        fieldMappings: {
            mainField: 'client_name',
            numberField: 'client_ref',
            searchFields: ['client_name', 'client_ref', 'source_type', 'pc_fname', 'pc_email', 'notes'],
            dueFields: {
                [workflowKey]: dueField,
            },
            statusFields: {
                [workflowKey]: statusField,
            },
            staffFields: {
                [workflowKey]: staffField,
            },
            planFields: {
                [workflowKey]: planField,
            }
        },
        validation: {
            numberMinLength: 1,
            requiredFields: ['source_type', 'source_id']
        },
        newRecordDefaults: {
            created_at: () => new Date().toISOString()
        },
        columns: [
            { key: 'client_name', label: 'Client', type: 'main', sortable: true },
            ...(singleSourceType ? [] : [{ key: 'source_type', label: 'Type', type: 'text', sortable: true }]),
            { key: 'client_ref', label: 'Ref', type: 'text', sortable: true },
            { key: dueField, label: dueLabel, type: 'deadline', sortable: true },
            { key: planField, label: 'Plan Month', type: 'select-month', sortable: true },
            { key: statusField, label: 'Status', type: 'status', workflow: workflowKey, sortable: true },
            { key: staffField, label: 'Assigned Staff', type: 'staff', role: workflowKey, sortable: true },
        ]
    };
};

// Updated: Added separate planner columns for ACC/TAX/CS
export const MODULES = {
  DASHBOARD: {
    id: 'dashboard',
    label: 'Dashboard',
    icon: BarChart3,
    colorClass: 'bg-slate-900',
    tableName: null, // No table for dashboard
    entity: {
        singular: 'Dashboard',
        plural: 'Dashboard',
        totalLabel: 'Staff Hub Dashboard',
        addLabel: '',
        numberLabel: '',
        syncLabel: ''
    },
    actions: {
        canAdd: false,
        canSync: false
    },
    workstreams: [],
    columns: [],
    fieldMappings: {},
    validation: {},
    newRecordDefaults: {}
  },
    STAFF_WORKLOAD: {
        id: 'staff_workload',
        label: 'Staff Workload',
        icon: UserCheck,
        colorClass: 'bg-slate-700',
        tableName: null,
        entity: {
                singular: 'Staff Workload',
                plural: 'Staff Workload',
                totalLabel: 'Staff Workload Dashboard',
                addLabel: '',
                numberLabel: '',
                syncLabel: ''
        },
        actions: {
                canAdd: false,
                canSync: false
        },
        workstreams: [],
        columns: [],
        fieldMappings: {},
        validation: {},
        newRecordDefaults: {}
    },
  COMPANY: {
    id: 'ch_company',
    label: 'CH Company',
    icon: Briefcase,
    colorClass: 'bg-purple-900',
    tableName: 'clients_coy',
    entity: {
        singular: 'Company',
        plural: 'Companies',
        totalLabel: 'Total CH Registered Clients',
        addLabel: 'Add Company',
        numberLabel: 'Company Number',
        syncLabel: 'Get CH Data'
    },
    actions: {
        canAdd: true,
        canSync: true
    },
    syncConfig: {
        functionName: 'update-companies-house',
        successMessage: 'Companies House data refresh triggered successfully! Data will be updated shortly.',
        errorLabel: 'Companies House refresh'
    },
    workstreams: [
        {
            key: 'cs',
            label: 'Confirmation Statement',
            shortLabel: 'CS',
            dueField: 'cs_due_date',
            planField: 'p_month_cs',
            statusField: 'status_cs',
            staffField: 'assigned_staff_cs_id',
            workflowTable: 'status_cs',
            refField: 'next_cs_made_up_to',
            nextField: 'next_cs_made_up_to',
            textColorClass: 'text-emerald-700',
            filterColorClass: 'bg-emerald-50 border-emerald-200 text-emerald-700',
            dotColorClass: 'bg-emerald-500'
        }
    ],
    workflowTables: {
        cs: 'status_cs'
    },
    fieldMappings: {
        // Main identifier fields
        mainField: 'company_name',
        numberField: 'company_number',
        // Searchable fields
        searchFields: ['company_name', 'company_number', 'inform_direct_urn', 'bm_urn', 'sp_client_folder', 'director_names', 'pc_pname', 'pc_fname'],
        // Due date fields for statistics and filtering
        dueFields: {
            cs: 'cs_due_date'
        },
        // Status fields
        statusFields: {
            cs: 'status_cs'
        },
        // Assigned staff fields
        staffFields: {
            cs: 'assigned_staff_cs_id'
        },
        // Planned month fields
        planFields: {
            cs: 'p_month_cs',
            legacy: 'planned_month'
        }
    },
    externalLinkSections: [
        {
            providerLabel: 'Inform Direct',
            sectionLabel: 'Inform Direct Links',
            referenceField: 'inform_direct_urn',
            referenceLabel: 'Inform Direct URN',
            referencePlaceholder: 'e.g. cLO5zFDZ',
            referenceHelp: 'Store the Inform Direct company reference once, then use the shortcuts below to jump straight into company secretarial screens.',
            emptyState: 'Add the Inform Direct URN to unlock direct shortcuts for this company.',
            editableReference: true,
            baseUrl: 'https://secure.informdirect.co.uk/c/',
            links: [
                { key: 'dashboard', label: 'Main Dashboard', suffix: '' },
                { key: 'company', label: 'Company Details', suffix: '/Company' },
                { key: 'confirmation_statement', label: 'Confirmation Statement Filing', suffix: '/ConfirmationStatement/MadeUp' },
                { key: 'officers', label: 'Officers', suffix: '/Officer' },
                { key: 'pscs', label: 'PSCs', suffix: '/Psc' },
            ]
        },
        {
            providerLabel: 'Companies House',
            sectionLabel: 'Companies House Links',
            referenceField: 'company_number',
            referenceLabel: 'Company Number',
            referenceHelp: 'Public Companies House shortcuts based on the registered company number already held in StaffHub.',
            emptyState: 'No company number is stored for this client yet.',
            editableReference: false,
            baseUrl: 'https://find-and-update.company-information.service.gov.uk/company/',
            links: [
                { key: 'overview', label: 'Company Overview', suffix: '' },
                { key: 'filing_history', label: 'Filing History', suffix: '/filing-history' },
                { key: 'officers', label: 'Officers', suffix: '/officers' },
                { key: 'pscs', label: 'Persons with Significant Control', suffix: '/persons-with-significant-control' },
            ]
        },
        createBrightManagerSection(),
        createClientFoldersSection(),
    ],
    validation: {
        numberMinLength: 8,
        requiredFields: ['company_number', 'company_name']
    },
    newRecordDefaults: {
        company_number: '',
        company_name: '',
        inform_direct_urn: '',
        bm_urn: '',
        sp_client_folder: '',
        created_at: () => new Date().toISOString()
    },
    columns: [
        { key: 'company_name', label: 'Company Name', type: 'main', sortable: true },
        { key: 'company_number', label: 'Number', type: 'text', sortable: true },
        { key: 'next_cs_made_up_to', label: 'NEXT (CS)', type: 'date', sortable: true },
        { key: 'cs_due_date', label: 'Due (CS)', type: 'deadline', sortable: true },
        { key: 'p_month_cs', label: 'PLAN (CS)', type: 'select-month', sortable: true },
        { key: 'status_cs', label: 'Status (CS)', type: 'status', workflow: 'cs', sortable: true },
        { key: 'assigned_staff_cs_id', label: 'Staff (CS)', type: 'staff', role: 'cs', sortable: true },
    ]
  },
    CH_ACCOUNTS: {
        id: 'ch_accounts',
        label: 'CH Accounts',
        icon: FileSpreadsheet,
        colorClass: 'bg-indigo-800',
        tableName: 'clients_ch_accounts',
        fetchTable: 'v_ch_accounts_clients',
        enrollMode: true,
        enrollServiceField: 'svc_accounts_prep',
        enrollTargetTable: 'clients_ch_accounts',
        enrollSourceTypes: ['company'],
        realtimeTables: ['clients_ch_accounts', 'clients_coy'],
        readonlyFields: [
            'client_name', 'client_ref', 'source_type', 'source_id',
            'pc_pname', 'pc_fname', 'pc_email', 'pc_tel',
            'accounts_ref_date', 'next_accounts_made_up_to',
        ],
        entity: {
            singular: 'CH Accounts Client',
            plural: 'CH Accounts Clients',
            totalLabel: 'Total CH Accounts Clients',
            addLabel: 'Enrol Company',
            numberLabel: 'Company Number',
            syncLabel: ''
        },
        actions: {
            canAdd: true,
            canSync: false
        },
        kpiConfig: {
            selectorLabel: 'Accounts Workflow',
            dueMonthLabel: 'Filing Month',
            progressTitle: 'Accounts Progress',
            staffLoadTitle: 'Accounts Staff Load',
            stageLabels: {
                not_started: 'Prep Not Started',
                in_progress: 'In Production',
                completed: 'Ready / Filed',
                overdue: 'Overdue Filing Risk',
            }
        },
        workstreams: [
            {
                key: 'accounts',
                label: 'Statutory Accounts',
                shortLabel: 'ACCOUNTS',
                dueField: 'accounts_due_date',
                planField: 'p_month_accounts',
                statusField: 'status_accounts',
                staffField: 'assigned_staff_accounts_id',
                workflowTable: 'status_acct',
                refField: 'accounts_ref_date',
                nextField: 'next_accounts_made_up_to',
                textColorClass: 'text-indigo-700',
                filterColorClass: 'bg-indigo-50 border-indigo-200 text-indigo-700',
                dotColorClass: 'bg-indigo-500'
            }
        ],
        workflowTables: {
            accounts: 'status_acct'
        },
        fieldMappings: {
            mainField: 'client_name',
            numberField: 'client_ref',
            searchFields: ['client_name', 'client_ref', 'pc_fname', 'pc_email', 'accounts_ref_date'],
            dueFields: {
                accounts: 'accounts_due_date'
            },
            statusFields: {
                accounts: 'status_accounts'
            },
            staffFields: {
                accounts: 'assigned_staff_accounts_id'
            },
            planFields: {
                accounts: 'p_month_accounts'
            }
        },
        validation: {
            numberMinLength: 1,
            requiredFields: ['source_type', 'source_id']
        },
        newRecordDefaults: {
            created_at: () => new Date().toISOString()
        },
        columns: [
            { key: 'client_name', label: 'Company', type: 'main', sortable: true },
            { key: 'client_ref', label: 'Number', type: 'text', sortable: true },
            { key: 'accounts_ref_date', label: 'REF', type: 'date-text', sortable: true },
            { key: 'next_accounts_made_up_to', label: 'NEXT', type: 'date', sortable: true },
            { key: 'accounts_due_date', label: 'Due (Accounts)', type: 'deadline', sortable: true },
            { key: 'p_month_accounts', label: 'Plan Month', type: 'select-month', sortable: true },
            { key: 'status_accounts', label: 'Status', type: 'status', workflow: 'accounts', sortable: true },
            { key: 'assigned_staff_accounts_id', label: 'Assigned Staff', type: 'staff', role: 'accounts', sortable: true },
        ]
    },
    COTAX: {
        id: 'cotax',
        label: 'CoTax',
        icon: Calculator,
        colorClass: 'bg-orange-800',
        tableName: 'clients_cotax',
        fetchTable: 'v_cotax_clients',
        enrollMode: true,
        enrollServiceField: 'svc_ct600',
        enrollTargetTable: 'clients_cotax',
        enrollSourceTypes: ['company'],
        realtimeTables: ['clients_cotax', 'clients_coy'],
        readonlyFields: [
            'client_name', 'client_ref', 'source_type', 'source_id',
            'pc_pname', 'pc_fname', 'pc_email', 'pc_tel',
            'accounts_ref_date', 'next_accounts_made_up_to',
        ],
        entity: {
            singular: 'CoTax Client',
            plural: 'CoTax Clients',
            totalLabel: 'Total CoTax Clients',
            addLabel: 'Enrol Company',
            numberLabel: 'Company Number',
            syncLabel: ''
        },
        actions: {
            canAdd: true,
            canSync: false
        },
        kpiConfig: {
            selectorLabel: 'CT600 Workflow',
            dueMonthLabel: 'Return Due Month',
            progressTitle: 'CT600 Progress',
            staffLoadTitle: 'CT600 Staff Load',
            stageLabels: {
                not_started: 'Draft Not Started',
                in_progress: 'In Preparation',
                completed: 'Ready / Filed',
                overdue: 'Overdue Return Risk',
            }
        },
        workstreams: [
            {
                key: 'cotax',
                label: 'Corporation Tax',
                shortLabel: 'CT600',
                dueField: 'ct600_due_date',
                planField: 'p_month_cotax',
                statusField: 'status_cotax',
                staffField: 'assigned_staff_cotax_id',
                workflowTable: 'status_tax',
                refField: 'accounts_ref_date',
                nextField: 'ct600_due_date',
                textColorClass: 'text-orange-700',
                filterColorClass: 'bg-orange-50 border-orange-200 text-orange-700',
                dotColorClass: 'bg-orange-500'
            }
        ],
        workflowTables: {
            cotax: 'status_tax'
        },
        fieldMappings: {
            mainField: 'client_name',
            numberField: 'client_ref',
            searchFields: ['client_name', 'client_ref', 'pc_fname', 'pc_email'],
            dueFields: {
                cotax: 'ct600_due_date'
            },
            statusFields: {
                cotax: 'status_cotax'
            },
            staffFields: {
                cotax: 'assigned_staff_cotax_id'
            },
            planFields: {
                cotax: 'p_month_cotax'
            }
        },
        validation: {
            numberMinLength: 1,
            requiredFields: ['source_type', 'source_id']
        },
        newRecordDefaults: {
            created_at: () => new Date().toISOString()
        },
        columns: [
            { key: 'client_name', label: 'Company', type: 'main', sortable: true },
            { key: 'client_ref', label: 'Number', type: 'text', sortable: true },
            { key: 'accounts_ref_date', label: 'Ref Date', type: 'date-text', sortable: true },
            { key: 'ct600_due_date', label: 'Due (CT600)', type: 'deadline', sortable: true },
            { key: 'p_month_cotax', label: 'Plan Month', type: 'select-month', sortable: true },
            { key: 'status_cotax', label: 'Status', type: 'status', workflow: 'cotax', sortable: true },
            { key: 'assigned_staff_cotax_id', label: 'Assigned Staff', type: 'staff', role: 'cotax', sortable: true },
        ]
    },
  CHARITY: {
    id: 'charity',
    label: 'Charity Clients',
    icon: Heart,
    colorClass: 'bg-sky-700',
    tableName: 'clients_charity',
    entity: {
        singular: 'Charity',
        plural: 'Charity Clients',
        totalLabel: 'Total Registered Charities',
        addLabel: 'Add Charity',
        numberLabel: 'Charity Number',
        syncLabel: 'Sync CC Data'
    },
    actions: {
        canAdd: true,
        canSync: true
    },
    syncConfig: {
        functionName: 'update-charity-commission',
        successMessage: 'Charity Commission sync triggered successfully! Data will be updated shortly.',
        errorLabel: 'Charity Commission sync'
    },
    workstreams: [
        {
            key: 'return',
            label: 'Annual Return',
            shortLabel: 'RETURN',
            dueField: 'annual_return_due_date',
            planField: 'p_month_return',
            statusField: 'status_return',
            staffField: 'assigned_staff_return_id',
            workflowTable: 'status_charity_return',
            refField: 'financial_period_end',
            nextField: 'financial_period_end',
            textColorClass: 'text-sky-700',
            filterColorClass: 'bg-sky-50 border-sky-200 text-sky-700',
            dotColorClass: 'bg-sky-500'
        },
        {
            key: 'accounts',
            label: 'Accounts',
            shortLabel: 'ACCOUNTS',
            dueField: 'accounts_due_date',
            planField: 'p_month_accounts',
            statusField: 'status_accounts',
            staffField: 'assigned_staff_accounts_id',
            workflowTable: 'status_charity_accounts',
            refField: 'financial_period_end',
            nextField: 'financial_period_end',
            textColorClass: 'text-indigo-700',
            filterColorClass: 'bg-indigo-50 border-indigo-200 text-indigo-700',
            dotColorClass: 'bg-indigo-500'
        }
    ],
    workflowTables: {
        return: 'status_charity_return',
        accounts: 'status_charity_accounts'
    },
    fieldMappings: {
        mainField: 'charity_name',
        numberField: 'charity_number',
        searchFields: ['charity_name', 'charity_number', 'bm_urn', 'sp_client_folder', 'cc_registered_name', 'pc_pname', 'pc_fname', 'pc_email'],
        dueFields: {
            return: 'annual_return_due_date',
            accounts: 'accounts_due_date'
        },
        statusFields: {
            return: 'status_return',
            accounts: 'status_accounts'
        },
        staffFields: {
            return: 'assigned_staff_return_id',
            accounts: 'assigned_staff_accounts_id'
        },
        planFields: {
            return: 'p_month_return',
            accounts: 'p_month_accounts',
            legacy: 'planned_month'
        }
    },
    externalLinkSections: [
        {
            providerLabel: 'Charity Commission',
            sectionLabel: 'Charity Commission Links',
            referenceField: 'charity_number',
            referenceLabel: 'Charity Number',
            referenceHelp: 'Public Charity Commission register shortcuts based on the registered charity number already held in StaffHub.',
            emptyState: 'No charity number is stored for this client yet.',
            editableReference: false,
            baseUrl: 'https://register-of-charities.charitycommission.gov.uk/en/charity-search/-/charity-details/',
            links: [
                { key: 'overview', label: 'Charity Overview', suffix: '/charity-overview' },
                { key: 'contact_information', label: 'Contact Information', suffix: '/contact-information' },
                { key: 'trustees', label: 'Trustees', suffix: '/trustees' },
                { key: 'financial_history', label: 'Financial History', suffix: '/financial-history' },
                { key: 'accounts_returns', label: 'Accounts & Annual Returns', suffix: '/accounts-and-annual-returns' },
            ]
        },
        createBrightManagerSection(),
        createClientFoldersSection(),
    ],
    validation: {
        numberMinLength: 3,
        requiredFields: ['charity_number', 'charity_name']
    },
    newRecordDefaults: {
        charity_number: '',
        charity_name: '',
        bm_urn: '',
        sp_client_folder: '',
        created_at: () => new Date().toISOString(),
        sync_status: 'manual'
    },
    columns: [
        { key: 'charity_name', label: 'Charity Name', type: 'main', sortable: true },
        { key: 'charity_number', label: 'Reg Number', type: 'text', sortable: true },
        { key: 'cc_registration_status', label: 'CC Status', type: 'text', sortable: true },
        { key: 'financial_period_end', label: 'Period End', type: 'date', sortable: true },
        { key: 'annual_return_due_date', label: 'Due (Return)', type: 'deadline', sortable: true },
        { key: 'accounts_due_date', label: 'Due (Acc)', type: 'deadline', sortable: true },
        { key: 'p_month_return', label: 'PLAN (RETURN)', type: 'select-month', sortable: true },
        { key: 'p_month_accounts', label: 'PLAN (ACC)', type: 'select-month', sortable: true },
        { key: 'status_return', label: 'Status (Return)', type: 'status', workflow: 'return', sortable: true },
        { key: 'status_accounts', label: 'Status (Acc)', type: 'status', workflow: 'accounts', sortable: true },
        { key: 'assigned_staff_return_id', label: 'Staff (Return)', type: 'staff', role: 'return', sortable: true },
        { key: 'assigned_staff_accounts_id', label: 'Staff (Acc)', type: 'staff', role: 'accounts', sortable: true },
    ]
  },
  UNREGISTERED: {
    id: 'unregistered',
    label: 'Unregistered Charity / Assoc',
    icon: Heart,
    colorClass: 'bg-teal-800',
    tableName: 'clients_unregistered',
    entity: {
        singular: 'Unregistered Entity',
        plural: 'Unregistered Entities',
        totalLabel: 'Total Unregistered Charity / Association Clients',
        addLabel: 'Add Unregistered Client',
        numberLabel: 'Entity Ref',
        syncLabel: ''
    },
    actions: {
        canAdd: true,
        canSync: false
    },
    workstreams: [
        {
            key: 'admin',
            label: 'Admin & Onboarding',
            shortLabel: 'ADMIN',
            dueField: 'review_due_date',
            planField: 'p_month_admin',
            statusField: 'status_admin',
            staffField: 'assigned_staff_admin_id',
            workflowTable: 'status_service_compliance',
            refField: 'review_due_date',
            nextField: 'review_due_date',
            textColorClass: 'text-teal-700',
            filterColorClass: 'bg-teal-50 border-teal-200 text-teal-700',
            dotColorClass: 'bg-teal-500'
        }
    ],
    workflowTables: {
        admin: 'status_service_compliance'
    },
    fieldMappings: {
        mainField: 'unregistered_name',
        numberField: 'unregistered_ref',
        searchFields: ['unregistered_name', 'unregistered_ref', 'bm_urn', 'sp_client_folder', 'pc_pname', 'pc_fname', 'pc_email'],
        dueFields: {
            admin: 'review_due_date'
        },
        statusFields: {
            admin: 'status_admin'
        },
        staffFields: {
            admin: 'assigned_staff_admin_id'
        },
        planFields: {
            admin: 'p_month_admin'
        }
    },
    externalLinkSections: [
        createBrightManagerSection(),
        createClientFoldersSection(),
    ],
    validation: {
        numberMinLength: 1,
        requiredFields: ['unregistered_name', 'unregistered_ref']
    },
    addModalFields: [
        { key: 'unregistered_ref', label: 'Entity Ref', placeholder: 'UCA-001', required: true, inputClassName: 'font-mono' },
        { key: 'unregistered_name', label: 'Entity Name', placeholder: 'Community Impact Group', required: true }
    ],
    onboardingPresets: [
        {
            id: 'uca_core_charity_ops',
            label: 'Core Charity Ops',
            description: 'Bookkeeping + accounts prep + gift aid + independent exam + consultation',
            values: {
                svc_bookkeeping: true,
                svc_accounts_prep: true,
                svc_gift_aid: true,
                svc_independent_exam: true,
                svc_consultation: true,
            }
        },
        {
            id: 'uca_employer_pack',
            label: 'Employer Pack',
            description: 'Payroll + pension auto-enrolment + bookkeeping + consultation',
            values: {
                svc_payroll: true,
                svc_pension_ae: true,
                svc_bookkeeping: true,
                svc_consultation: true,
            }
        },
        {
            id: 'uca_advisory_finance',
            label: 'Advisory Finance',
            description: 'CFO + management accounts + VAT + consultation',
            values: {
                svc_cfo: true,
                svc_mgmt_accounts: true,
                svc_vat_returns: true,
                svc_consultation: true,
            }
        }
    ],
    newRecordDefaults: {
        status_admin: 'Not Started',
        bm_urn: '',
        sp_client_folder: '',
        svc_ct600: false,
        svc_confirmation_statement: false,
        svc_self_assessment: false,
        svc_mtd_itsa: false,
        svc_registered_address: false,
        svc_charity_commission: false,
        created_at: () => new Date().toISOString()
    },
    columns: [
        { key: 'unregistered_name', label: 'Entity Name', type: 'main', sortable: true },
        { key: 'unregistered_ref', label: 'Ref', type: 'text', sortable: true },
        { key: 'review_due_date', label: 'Review Due', type: 'deadline', sortable: true },
        { key: 'p_month_admin', label: 'Plan Month', type: 'select-month', sortable: true },
        { key: 'status_admin', label: 'Status', type: 'status', workflow: 'admin', sortable: true },
        { key: 'assigned_staff_admin_id', label: 'Assigned Staff', type: 'staff', role: 'admin', sortable: true },
    ]
  },
  PARTNERSHIP: {
    id: 'partnership',
    label: 'Partnerships',
    icon: Briefcase,
    colorClass: 'bg-amber-800',
    tableName: 'clients_partnership',
    entity: {
        singular: 'Partnership',
        plural: 'Partnerships',
        totalLabel: 'Total Partnership Clients',
        addLabel: 'Add Partnership',
        numberLabel: 'Partnership Ref',
        syncLabel: ''
    },
    actions: {
        canAdd: true,
        canSync: false
    },
    workstreams: [
        {
            key: 'admin',
            label: 'Admin & Onboarding',
            shortLabel: 'ADMIN',
            dueField: 'review_due_date',
            planField: 'p_month_admin',
            statusField: 'status_admin',
            staffField: 'assigned_staff_admin_id',
            workflowTable: 'status_service_compliance',
            refField: 'review_due_date',
            nextField: 'review_due_date',
            textColorClass: 'text-amber-700',
            filterColorClass: 'bg-amber-50 border-amber-200 text-amber-700',
            dotColorClass: 'bg-amber-500'
        }
    ],
    workflowTables: {
        admin: 'status_service_compliance'
    },
    fieldMappings: {
        mainField: 'partnership_name',
        numberField: 'partnership_ref',
        searchFields: ['partnership_name', 'partnership_ref', 'bm_urn', 'sp_client_folder', 'pc_pname', 'pc_fname', 'pc_email'],
        dueFields: {
            admin: 'review_due_date'
        },
        statusFields: {
            admin: 'status_admin'
        },
        staffFields: {
            admin: 'assigned_staff_admin_id'
        },
        planFields: {
            admin: 'p_month_admin'
        }
    },
    externalLinkSections: [
        createBrightManagerSection(),
        createClientFoldersSection(),
    ],
    validation: {
        numberMinLength: 1,
        requiredFields: ['partnership_name', 'partnership_ref']
    },
    addModalFields: [
        { key: 'partnership_ref', label: 'Partnership Ref', placeholder: 'PART-001', required: true, inputClassName: 'font-mono' },
        { key: 'partnership_name', label: 'Partnership Name', placeholder: 'A&B Property Partners', required: true }
    ],
    onboardingPresets: [
        {
            id: 'partnership_compliance_core',
            label: 'Compliance Core',
            description: 'Bookkeeping + accounts prep + VAT + CIS + consultation',
            values: {
                svc_bookkeeping: true,
                svc_accounts_prep: true,
                svc_vat_returns: true,
                svc_cis: true,
                svc_consultation: true,
            }
        },
        {
            id: 'partnership_employer_pack',
            label: 'Employer Pack',
            description: 'Payroll + pension auto-enrolment + bookkeeping + consultation',
            values: {
                svc_payroll: true,
                svc_pension_ae: true,
                svc_bookkeeping: true,
                svc_consultation: true,
            }
        },
        {
            id: 'partnership_growth_advisory',
            label: 'Growth Advisory',
            description: 'CFO + management accounts + consultation',
            values: {
                svc_cfo: true,
                svc_mgmt_accounts: true,
                svc_consultation: true,
            }
        }
    ],
    newRecordDefaults: {
        status_admin: 'Not Started',
        bm_urn: '',
        sp_client_folder: '',
        svc_ct600: false,
        svc_charity_commission: false,
        svc_confirmation_statement: false,
        svc_self_assessment: false,
        svc_mtd_itsa: false,
        svc_gift_aid: false,
        svc_independent_exam: false,
        created_at: () => new Date().toISOString()
    },
    columns: [
        { key: 'partnership_name', label: 'Partnership Name', type: 'main', sortable: true },
        { key: 'partnership_ref', label: 'Ref', type: 'text', sortable: true },
        { key: 'review_due_date', label: 'Review Due', type: 'deadline', sortable: true },
        { key: 'p_month_admin', label: 'Plan Month', type: 'select-month', sortable: true },
        { key: 'status_admin', label: 'Status', type: 'status', workflow: 'admin', sortable: true },
        { key: 'assigned_staff_admin_id', label: 'Assigned Staff', type: 'staff', role: 'admin', sortable: true },
    ]
  },
  INDIVIDUAL: {
    id: 'individual',
    label: 'Individual',
    icon: UserCheck,
    colorClass: 'bg-emerald-800',
    tableName: 'clients_individual',
    entity: {
        totalLabel: 'Total Registered Individuals',
        addLabel: 'Add Individual',
        numberLabel: 'UTRN',
        syncLabel: 'Sync HMRC Data'
    },
    actions: {
        canAdd: true,
        canSync: true
    },
    syncConfig: {
        functionName: 'update-individual-hmrc',
        successMessage: 'HMRC sync triggered successfully! Individual records will refresh shortly.',
        errorLabel: 'HMRC sync'
    },
    workstreams: [
        {
            key: 'records',
            label: 'Records & Books',
            shortLabel: 'RECORDS',
            dueField: 'records_due_date',
            planField: 'p_month_records',
            statusField: 'status_records',
            staffField: 'assigned_staff_records_id',
            workflowTable: 'status_individual_records',
            refField: 'sa_tax_year_end',
            nextField: 'sa_tax_year_end',
            textColorClass: 'text-green-600',
            filterColorClass: 'bg-green-100 border-green-300',
            dotColorClass: 'bg-green-500'
        },
        {
            key: 'planning',
            label: 'Tax Planning',
            shortLabel: 'PLANNING',
            dueField: null,
            planField: 'p_month_planning',
            statusField: 'status_planning',
            staffField: 'assigned_staff_planning_id',
            workflowTable: 'status_individual_planning',
            refField: 'sa_tax_year_end',
            nextField: 'sa_tax_year_end',
            textColorClass: 'text-emerald-600',
            filterColorClass: 'bg-emerald-100 border-emerald-300',
            dotColorClass: 'bg-emerald-500'
        }
    ],
    workflowTables: {
        records: 'status_individual_records',
        planning: 'status_individual_planning'
    },
    fieldMappings: {
        mainField: 'individual_name',
        numberField: 'individual_utr',
        searchFields: ['individual_name', 'individual_utr', 'bm_urn', 'sp_client_folder'],
        dueFields: {
            records: 'records_due_date'
        },
        statusFields: {
            records: 'status_records',
            planning: 'status_planning'
        },
        staffFields: {
            records: 'assigned_staff_records_id',
            planning: 'assigned_staff_planning_id'
        },
        planFields: {
            records: 'p_month_records',
            planning: 'p_month_planning'
        }
    },
    externalLinkSections: [
        createBrightManagerSection(),
        createClientFoldersSection(),
    ],
    validation: {
        numberMinLength: 3,
        requiredFields: ['individual_name', 'individual_utr']
    },
    addModalFields: [
        { key: 'individual_utr', label: 'UTRN', placeholder: '2714614479', required: true, inputClassName: 'font-mono' },
        { key: 'individual_name', label: 'Client Name', placeholder: 'Zara Oliver', required: true },
        { key: 'individual_ni_number', label: 'NINO', placeholder: 'WN197163A', inputClassName: 'font-mono' }
    ],
    newRecordDefaults: {
        individual_name: '',
        individual_utr: '',
        bm_urn: '',
        sp_client_folder: '',
        individual_ni_number: '',
        individual_dob: null,
        sa_due_date: () => {
            const date = new Date();
            date.setUTCFullYear(date.getUTCFullYear());
            date.setUTCMonth(0);
            date.setUTCDate(31);
            return date.toISOString().split('T')[0];
        },
        created_at: () => new Date().toISOString()
    },
    columns: [
        { key: 'individual_name', label: 'Name', type: 'main', sortable: true },
        { key: 'individual_utr', label: 'UTRN', type: 'text', sortable: true },
        { key: 'hmrc_last_tax_year_filed', label: 'Last Tax Year', type: 'text', sortable: true },
        { key: 'hmrc_next_tax_year', label: 'Next Tax Year', type: 'text', sortable: true },
        { key: 'records_due_date', label: 'Due (Records)', type: 'deadline', sortable: true },
        { key: 'p_month_records', label: 'PLAN (Records)', type: 'select-month', sortable: true },
        { key: 'p_month_planning', label: 'PLAN (Tax)', type: 'select-month', sortable: true },
        { key: 'status_records', label: 'Status (Records)', type: 'status', workflow: 'records', sortable: true },
        { key: 'status_planning', label: 'Status (Planning)', type: 'status', workflow: 'planning', sortable: true },
        { key: 'assigned_staff_records_id', label: 'Staff (Records)', type: 'staff', role: 'records', sortable: true },
        { key: 'assigned_staff_planning_id', label: 'Staff (Planning)', type: 'staff', role: 'planning', sortable: true }
    ]
  },
    // Additional modules
  MTD: { 
      id: 'mtd_itsa', 
      label: 'MTD ITSA', 
      icon: Calculator, 
      colorClass: 'bg-orange-700', 
      tableName: 'clients_individual', 
      entity: {
          singular: 'MTD ITSA Client',
          plural: 'MTD ITSA Clients',
          totalLabel: 'Total MTD ITSA In-Scope Clients',
          addLabel: 'Add MTD Client',
          numberLabel: 'UTRN',
          syncLabel: 'Sync HMRC MTD'
      },
      actions: {
          canAdd: false,
          canSync: true
      },
      syncConfig: {
          functionName: 'update-individual-hmrc',
          successMessage: 'HMRC MTD ITSA sync triggered successfully. Status and deadlines will refresh shortly.',
          errorLabel: 'HMRC MTD ITSA sync'
      },
      // Restrict list to clients in MTD ITSA scope
      postFetchFilter: (record) => {
          const status = String(record?.hmrc_itsa_status || '').trim().toLowerCase();
          if (!status) return false;

          const isMtdStatus = status.includes('mtd');
          const isAffirmative =
              status.includes('mandated') ||
              status.includes('voluntar') ||
              status.includes('joined') ||
              status.includes('in scope') ||
              status.includes('required');

          return isMtdStatus && isAffirmative;
      },
      workstreams: [
          {
              key: 'q1',
              label: 'Quarter 1',
              shortLabel: 'Q1',
              dueField: 'mtd_q1_due_date',
              planField: 'mtd_plan_q1',
              statusField: 'mtd_status_q1',
              staffField: 'mtd_staff_q1_id',
              workflowTable: 'status_mtd_q1',
              refField: 'hmrc_next_tax_year',
              nextField: 'mtd_q1_due_date',
              textColorClass: 'text-emerald-700',
              filterColorClass: 'bg-emerald-50 border-emerald-200 text-emerald-700',
              dotColorClass: 'bg-emerald-500'
          },
          {
              key: 'q2',
              label: 'Quarter 2',
              shortLabel: 'Q2',
              dueField: 'mtd_q2_due_date',
              planField: 'mtd_plan_q2',
              statusField: 'mtd_status_q2',
              staffField: 'mtd_staff_q2_id',
              workflowTable: 'status_mtd_q2',
              refField: 'hmrc_next_tax_year',
              nextField: 'mtd_q2_due_date',
              textColorClass: 'text-cyan-700',
              filterColorClass: 'bg-cyan-50 border-cyan-200 text-cyan-700',
              dotColorClass: 'bg-cyan-500'
          },
          {
              key: 'q3',
              label: 'Quarter 3',
              shortLabel: 'Q3',
              dueField: 'mtd_q3_due_date',
              planField: 'mtd_plan_q3',
              statusField: 'mtd_status_q3',
              staffField: 'mtd_staff_q3_id',
              workflowTable: 'status_mtd_q3',
              refField: 'hmrc_next_tax_year',
              nextField: 'mtd_q3_due_date',
              textColorClass: 'text-violet-700',
              filterColorClass: 'bg-violet-50 border-violet-200 text-violet-700',
              dotColorClass: 'bg-violet-500'
          },
          {
              key: 'q4',
              label: 'Quarter 4',
              shortLabel: 'Q4',
              dueField: 'mtd_q4_due_date',
              planField: 'mtd_plan_q4',
              statusField: 'mtd_status_q4',
              staffField: 'mtd_staff_q4_id',
              workflowTable: 'status_mtd_q4',
              refField: 'hmrc_next_tax_year',
              nextField: 'mtd_q4_due_date',
              textColorClass: 'text-amber-700',
              filterColorClass: 'bg-amber-50 border-amber-200 text-amber-700',
              dotColorClass: 'bg-amber-500'
          },
          {
              key: 'final',
              label: 'Final Declaration',
              shortLabel: 'FINAL',
              dueField: 'mtd_final_declaration_due',
              planField: 'mtd_plan_final',
              statusField: 'mtd_status_final',
              staffField: 'mtd_staff_final_id',
              workflowTable: 'status_mtd_final',
              refField: 'hmrc_next_tax_year',
              nextField: 'mtd_final_declaration_due',
              textColorClass: 'text-blue-700',
              filterColorClass: 'bg-blue-50 border-blue-200 text-blue-700',
              dotColorClass: 'bg-blue-500'
          }
      ],
      workflowTables: {
          q1: 'status_mtd_q1',
          q2: 'status_mtd_q2',
          q3: 'status_mtd_q3',
          q4: 'status_mtd_q4',
          final: 'status_mtd_final'
      },
      fieldMappings: {
          mainField: 'individual_name',
          numberField: 'individual_utr',
          searchFields: ['individual_name', 'individual_utr', 'hmrc_itsa_status', 'hmrc_agent_auth_status'],
          dueFields: {
              q1: 'mtd_q1_due_date',
              q2: 'mtd_q2_due_date',
              q3: 'mtd_q3_due_date',
              q4: 'mtd_q4_due_date',
              final: 'mtd_final_declaration_due'
          },
          statusFields: {
              q1: 'mtd_status_q1',
              q2: 'mtd_status_q2',
              q3: 'mtd_status_q3',
              q4: 'mtd_status_q4',
              final: 'mtd_status_final'
          },
          staffFields: {
              q1: 'mtd_staff_q1_id',
              q2: 'mtd_staff_q2_id',
              q3: 'mtd_staff_q3_id',
              q4: 'mtd_staff_q4_id',
              final: 'mtd_staff_final_id'
          },
          planFields: {
              q1: 'mtd_plan_q1',
              q2: 'mtd_plan_q2',
              q3: 'mtd_plan_q3',
              q4: 'mtd_plan_q4',
              final: 'mtd_plan_final'
          }
      },
      validation: {
          numberMinLength: 3,
          requiredFields: ['individual_name', 'individual_utr']
      },
      addModalFields: [
          { key: 'individual_utr', label: 'UTRN', placeholder: '2714614479', required: true, inputClassName: 'font-mono' },
          { key: 'individual_name', label: 'Client Name', placeholder: 'Alex Morgan', required: true },
          { key: 'individual_ni_number', label: 'NINO', placeholder: 'WN197163A', inputClassName: 'font-mono' }
      ],
      newRecordDefaults: {
          individual_name: '',
          individual_utr: '',
          individual_ni_number: '',
          hmrc_itsa_status: 'MTD Voluntarily Joined',
          hmrc_agent_auth_status: 'Not Authorised',
          created_at: () => new Date().toISOString()
      },
      columns: [
          { key: 'individual_name', label: 'Client', type: 'main', sortable: true },
          { key: 'individual_utr', label: 'UTRN', type: 'text', sortable: true },
          { key: 'hmrc_sync_health', label: 'Sync Health', type: 'sync-health', sortable: true },
          { key: 'hmrc_itsa_status', label: 'ITSA Scope', type: 'text', sortable: true },
          { key: 'hmrc_agent_auth_status', label: 'Agent Auth', type: 'text', sortable: true },
          { key: 'hmrc_next_tax_year', label: 'Next Tax Year', type: 'text', sortable: true },
          { key: 'mtd_next_obligation_label', label: 'Next Obligation', type: 'text', sortable: true },
          { key: 'mtd_next_obligation_due', label: 'Due (Next)', type: 'deadline', sortable: true },
          { key: 'mtd_q1_due_date', label: 'Q1 Due', type: 'date', sortable: true },
          { key: 'mtd_q2_due_date', label: 'Q2 Due', type: 'date', sortable: true },
          { key: 'mtd_q3_due_date', label: 'Q3 Due', type: 'date', sortable: true },
          { key: 'mtd_q4_due_date', label: 'Q4 Due', type: 'date', sortable: true },
          { key: 'mtd_final_declaration_due', label: 'Final Due', type: 'deadline', sortable: true },
          { key: 'mtd_plan_q1', label: 'PLAN (Q1)', type: 'select-month', sortable: true },
          { key: 'mtd_plan_q2', label: 'PLAN (Q2)', type: 'select-month', sortable: true },
          { key: 'mtd_plan_q3', label: 'PLAN (Q3)', type: 'select-month', sortable: true },
          { key: 'mtd_plan_q4', label: 'PLAN (Q4)', type: 'select-month', sortable: true },
          { key: 'mtd_plan_final', label: 'PLAN (FINAL)', type: 'select-month', sortable: true },
          { key: 'mtd_status_q1', label: 'Status (Q1)', type: 'status', workflow: 'q1', sortable: true },
          { key: 'mtd_status_q2', label: 'Status (Q2)', type: 'status', workflow: 'q2', sortable: true },
          { key: 'mtd_status_q3', label: 'Status (Q3)', type: 'status', workflow: 'q3', sortable: true },
          { key: 'mtd_status_q4', label: 'Status (Q4)', type: 'status', workflow: 'q4', sortable: true },
          { key: 'mtd_status_final', label: 'Status (Final)', type: 'status', workflow: 'final', sortable: true },
          { key: 'mtd_staff_q1_id', label: 'Staff (Q1)', type: 'staff', role: 'q1', sortable: true },
          { key: 'mtd_staff_q2_id', label: 'Staff (Q2)', type: 'staff', role: 'q2', sortable: true },
          { key: 'mtd_staff_q3_id', label: 'Staff (Q3)', type: 'staff', role: 'q3', sortable: true },
          { key: 'mtd_staff_q4_id', label: 'Staff (Q4)', type: 'staff', role: 'q4', sortable: true },
          { key: 'mtd_staff_final_id', label: 'Staff (Final)', type: 'staff', role: 'final', sortable: true }
      ]
  },
  BOOKKEEPING: {
      id: 'bookkeeping',
      label: 'Bookkeeping',
      icon: BookOpen,
      colorClass: 'bg-teal-700',
      tableName: 'clients_books',          // write target — all mutations go here
      fetchTable: 'v_bookkeeping_clients', // read source — resolved JOIN view
      enrollMode: true,                    // use EnrolClientModal, not AddCompanyModal
      enrollServiceField: 'svc_bookkeeping',
      enrollTargetTable: 'clients_books',
    enrollSourceTypes: ['company', 'charity', 'individual', 'unregistered', 'partnership'],
    realtimeTables: ['clients_books', 'clients_coy', 'clients_charity', 'clients_individual', 'clients_unregistered', 'clients_partnership'],
      // Fields that live in source tables — never written back via this module
      readonlyFields: [
          'client_name', 'client_ref', 'source_type', 'source_id',
          'pc_pname', 'pc_fname', 'pc_email', 'pc_tel',
      ],
      entity: {
          singular: 'Bookkeeping Client',
          plural: 'Bookkeeping Clients',
          totalLabel: 'Total Bookkeeping Clients',
          addLabel: 'Enrol Client',
          numberLabel: 'Client Ref',
          syncLabel: ''
      },
      actions: {
          canAdd: true,
          canSync: false
      },
      workstreams: [
          {
              key: 'books',
              label: 'Bookkeeping',
              shortLabel: 'BOOKS',
              dueField: 'bk_due_date',
              planField: 'p_month_books',
              statusField: 'status_books',
              staffField: 'assigned_staff_books_id',
              workflowTable: 'status_books_bk',
              refField: 'period_reference',
              nextField: 'bk_due_date',
              textColorClass: 'text-teal-700',
              filterColorClass: 'bg-teal-50 border-teal-200 text-teal-700',
              dotColorClass: 'bg-teal-500'
          }
      ],
      workflowTables: {
          books: 'status_books_bk'
      },
      fieldMappings: {
          mainField: 'client_name',
          numberField: 'client_ref',
          searchFields: [
              'client_name', 'client_ref', 'source_type',
              'bookkeeping_software', 'bookkeeping_frequency',
              'period_reference', 'pc_fname', 'pc_email'
          ],
          dueFields: {
              books: 'bk_due_date'
          },
          statusFields: {
              books: 'status_books'
          },
          staffFields: {
              books: 'assigned_staff_books_id'
          },
          planFields: {
              books: 'p_month_books'
          }
      },
      validation: {
          numberMinLength: 1,
          requiredFields: ['source_type', 'source_id']
      },
      newRecordDefaults: {
          bookkeeping_software:  'xero',
          bookkeeping_frequency: 'monthly',
          vat_registered:        false,
          created_at: () => new Date().toISOString()
      },
      columns: [
          { key: 'client_name',            label: 'Client',         type: 'main',         sortable: true },
          { key: 'source_type',            label: 'Type',           type: 'text',         sortable: true },
          { key: 'client_ref',             label: 'Ref',            type: 'text',         sortable: true },
          { key: 'bookkeeping_software',   label: 'Software',       type: 'text',         sortable: true },
          { key: 'bookkeeping_frequency',  label: 'Frequency',      type: 'text',         sortable: true },
          { key: 'period_reference',       label: 'Period',         type: 'text',         sortable: true },
          { key: 'bk_due_date',            label: 'Due Date',       type: 'deadline',     sortable: true },
          { key: 'p_month_books',          label: 'Plan Month',     type: 'select-month', sortable: true },
          { key: 'status_books',           label: 'Status',         type: 'status',       workflow: 'books', sortable: true },
          { key: 'assigned_staff_books_id',label: 'Assigned Staff', type: 'staff',        role: 'books',     sortable: true }
      ]
  },
  PAYROLL: createRelationalServiceModule({
      id: 'payroll',
      label: 'Payroll',
      icon: Banknote,
      colorClass: 'bg-pink-700',
      tableName: 'clients_payroll',
      fetchTable: 'v_payroll_clients',
      enrollServiceField: 'svc_payroll',
    sourceTypes: ['company', 'charity', 'individual', 'unregistered', 'partnership'],
    workflowTable: 'status_payroll',
      workflowKey: 'payroll',
      workflowLabel: 'Payroll',
      shortLabel: 'PAYROLL',
      dueLabel: 'Due (Payroll)',
      textColorClass: 'text-pink-700',
      filterColorClass: 'bg-pink-50 border-pink-200 text-pink-700',
      dotColorClass: 'bg-pink-500',
      kpiConfig: {
          selectorLabel: 'Payroll Workflow',
          dueMonthLabel: 'Payroll Month',
          progressTitle: 'Payroll Progress',
          staffLoadTitle: 'Payroll Staff Load',
          stageLabels: {
              not_started: 'Inputs Pending',
              in_progress: 'In Processing',
              completed: 'Submitted / Done',
              overdue: 'Overdue Payroll',
          },
      },
  }),
  PENSION_AE: createRelationalServiceModule({
      id: 'pension_ae',
      label: 'Pension Auto-Enrolment',
      icon: Briefcase,
      colorClass: 'bg-violet-700',
      tableName: 'clients_pension_ae',
      fetchTable: 'v_pension_ae_clients',
      enrollServiceField: 'svc_pension_ae',
    sourceTypes: ['company', 'charity', 'individual', 'unregistered', 'partnership'],
    workflowTable: 'status_pension_ae',
      workflowKey: 'pension',
      workflowLabel: 'Pension Auto-Enrolment',
      shortLabel: 'PAE',
      dueLabel: 'Due (PAE)',
      textColorClass: 'text-violet-700',
      filterColorClass: 'bg-violet-50 border-violet-200 text-violet-700',
      dotColorClass: 'bg-violet-500',
      kpiConfig: {
          selectorLabel: 'PAE Workflow',
          dueMonthLabel: 'Compliance Month',
          progressTitle: 'PAE Progress',
          staffLoadTitle: 'PAE Staff Load',
          stageLabels: {
              not_started: 'Assessment Pending',
              in_progress: 'In Assessment',
              completed: 'Submitted / Done',
              overdue: 'Overdue Compliance',
          },
      },
  }),
  VAT: createRelationalServiceModule({
      id: 'vat',
      label: 'VAT Returns',
      icon: Percent,
      colorClass: 'bg-indigo-800',
      tableName: 'clients_vat',
      fetchTable: 'v_vat_clients',
      enrollServiceField: 'svc_vat_returns',
    sourceTypes: ['company', 'charity', 'individual', 'unregistered', 'partnership'],
    workflowTable: 'status_vat_returns',
      workflowKey: 'vat',
      workflowLabel: 'VAT Returns',
      shortLabel: 'VAT',
      dueLabel: 'Due (VAT)',
      textColorClass: 'text-indigo-700',
      filterColorClass: 'bg-indigo-50 border-indigo-200 text-indigo-700',
      dotColorClass: 'bg-indigo-500',
      kpiConfig: {
          selectorLabel: 'VAT Workflow',
          dueMonthLabel: 'Return Month',
          progressTitle: 'VAT Progress',
          staffLoadTitle: 'VAT Staff Load',
          stageLabels: {
              not_started: 'Books Pending',
              in_progress: 'Return In Progress',
              completed: 'Filed / Done',
              overdue: 'Overdue VAT',
          },
      },
  }),
  CFO: createRelationalServiceModule({
      id: 'cfo',
      label: 'CFO',
      icon: PieChart,
      colorClass: 'bg-cyan-800',
      tableName: 'clients_cfo',
      fetchTable: 'v_cfo_clients',
      enrollServiceField: 'svc_cfo',
    sourceTypes: ['company', 'unregistered', 'partnership'],
    workflowTable: 'status_cfo',
      workflowKey: 'cfo',
      workflowLabel: 'CFO Advisory',
      shortLabel: 'CFO',
      dueLabel: 'Next Review',
      textColorClass: 'text-cyan-700',
      filterColorClass: 'bg-cyan-50 border-cyan-200 text-cyan-700',
      dotColorClass: 'bg-cyan-500',
      kpiConfig: {
          selectorLabel: 'CFO Workflow',
          dueMonthLabel: 'Review Month',
          progressTitle: 'CFO Progress',
          staffLoadTitle: 'CFO Staff Load',
          stageLabels: {
              not_started: 'Planning Stage',
              in_progress: 'In Advisory',
              completed: 'Delivered / Closed',
              overdue: 'Overdue Review',
          },
      },
  }),
  MGMT_ACCOUNTS: createRelationalServiceModule({
      id: 'mgmt_accounts',
      label: 'Management Accounts',
      icon: FileSpreadsheet,
      colorClass: 'bg-sky-800',
      tableName: 'clients_mgmt_accounts',
      fetchTable: 'v_mgmt_accounts_clients',
      enrollServiceField: 'svc_mgmt_accounts',
    sourceTypes: ['company', 'unregistered', 'partnership'],
    workflowTable: 'status_mgmt_accounts',
      workflowKey: 'mgmt',
      workflowLabel: 'Management Accounts',
      shortLabel: 'MGMT',
      dueLabel: 'Due (Mgmt)',
      textColorClass: 'text-sky-700',
      filterColorClass: 'bg-sky-50 border-sky-200 text-sky-700',
      dotColorClass: 'bg-sky-500',
      kpiConfig: {
          selectorLabel: 'Mgmt Workflow',
          dueMonthLabel: 'Pack Month',
          progressTitle: 'Mgmt Accounts Progress',
          staffLoadTitle: 'Mgmt Staff Load',
          stageLabels: {
              not_started: 'Books Pending',
              in_progress: 'Pack In Progress',
              completed: 'Delivered / Done',
              overdue: 'Overdue Pack',
          },
      },
  }),
  SA_RETURNS: createRelationalServiceModule({
      id: 'sa_returns',
      label: 'SA Returns',
      icon: UserCheck,
      colorClass: 'bg-orange-700',
      tableName: 'clients_sa_returns',
      fetchTable: 'v_sa_returns_clients',
      enrollServiceField: 'svc_self_assessment',
      sourceTypes: ['individual'],
    workflowTable: 'status_sa_returns',
      workflowKey: 'sa',
      workflowLabel: 'Self Assessment',
      shortLabel: 'SA',
      dueLabel: 'Due (SA)',
      textColorClass: 'text-orange-700',
      filterColorClass: 'bg-orange-50 border-orange-200 text-orange-700',
      dotColorClass: 'bg-orange-500',
      kpiConfig: {
          selectorLabel: 'SA Workflow',
          dueMonthLabel: 'Filing Month',
          progressTitle: 'SA Progress',
          staffLoadTitle: 'SA Staff Load',
          stageLabels: {
              not_started: 'Records Pending',
              in_progress: 'Return In Progress',
              completed: 'Filed / Done',
              overdue: 'Overdue SA',
          },
      },
  }),
  CIS: createRelationalServiceModule({
      id: 'cis',
      label: 'CIS',
      icon: Briefcase,
      colorClass: 'bg-amber-700',
      tableName: 'clients_cis',
      fetchTable: 'v_cis_clients',
      enrollServiceField: 'svc_cis',
    sourceTypes: ['company', 'individual', 'unregistered', 'partnership'],
    workflowTable: 'status_cis',
      workflowKey: 'cis',
      workflowLabel: 'CIS',
      shortLabel: 'CIS',
      dueLabel: 'Due (CIS)',
      textColorClass: 'text-amber-700',
      filterColorClass: 'bg-amber-50 border-amber-200 text-amber-700',
      dotColorClass: 'bg-amber-500',
      kpiConfig: {
          selectorLabel: 'CIS Workflow',
          dueMonthLabel: 'Return Month',
          progressTitle: 'CIS Progress',
          staffLoadTitle: 'CIS Staff Load',
          stageLabels: {
              not_started: 'Data Pending',
              in_progress: 'Return In Progress',
              completed: 'Filed / Done',
              overdue: 'Overdue CIS',
          },
      },
  }),
  GIFT: createRelationalServiceModule({
      id: 'gift_aid',
      label: 'Gift Aid',
      icon: Gift,
      colorClass: 'bg-fuchsia-800',
      tableName: 'clients_gift',
      fetchTable: 'v_gift_aid_clients',
      enrollServiceField: 'svc_gift_aid',
    sourceTypes: ['charity', 'unregistered'],
    workflowTable: 'status_gift_aid',
      workflowKey: 'gift',
      workflowLabel: 'Gift Aid',
      shortLabel: 'GIFT',
      dueLabel: 'Due (Gift Aid)',
      textColorClass: 'text-fuchsia-700',
      filterColorClass: 'bg-fuchsia-50 border-fuchsia-200 text-fuchsia-700',
      dotColorClass: 'bg-fuchsia-500',
      kpiConfig: {
          selectorLabel: 'Gift Aid Workflow',
          dueMonthLabel: 'Claim Month',
          progressTitle: 'Gift Aid Progress',
          staffLoadTitle: 'Gift Aid Staff Load',
          stageLabels: {
              not_started: 'Data Pending',
              in_progress: 'Claim In Progress',
              completed: 'Submitted / Done',
              overdue: 'Overdue Claim',
          },
      },
  }),
  ADDRESS: createRelationalServiceModule({
      id: 'reg_address',
      label: 'Registered Address',
      icon: MapPin,
      colorClass: 'bg-rose-800',
      tableName: 'clients_address',
      fetchTable: 'v_registered_address_clients',
      enrollServiceField: 'svc_registered_address',
    sourceTypes: ['company', 'partnership'],
    workflowTable: 'status_registered_address',
      workflowKey: 'address',
      workflowLabel: 'Registered Address',
      shortLabel: 'ADDR',
      dueLabel: 'Renewal Due',
      textColorClass: 'text-rose-700',
      filterColorClass: 'bg-rose-50 border-rose-200 text-rose-700',
      dotColorClass: 'bg-rose-500',
      kpiConfig: {
          selectorLabel: 'Address Workflow',
          dueMonthLabel: 'Renewal Month',
          progressTitle: 'Registered Address Progress',
          staffLoadTitle: 'Address Staff Load',
          stageLabels: {
              not_started: 'Renewal Pending',
              in_progress: 'Renewal In Progress',
              completed: 'Renewed / Done',
              overdue: 'Overdue Renewal',
          },
      },
  }),
  CONSULTATION: createRelationalServiceModule({
      id: 'consultation',
      label: 'Consultation / Advice',
      icon: FolderOpen,
      colorClass: 'bg-slate-700',
      tableName: 'clients_consultation',
      fetchTable: 'v_consultation_clients',
      enrollServiceField: 'svc_consultation',
    sourceTypes: ['company', 'charity', 'individual', 'unregistered', 'partnership'],
    workflowTable: 'status_consultation',
      workflowKey: 'consultation',
      workflowLabel: 'Consultation / Advice',
      shortLabel: 'ADVICE',
      dueLabel: 'Next Review',
      textColorClass: 'text-slate-700',
      filterColorClass: 'bg-slate-50 border-slate-200 text-slate-700',
      dotColorClass: 'bg-slate-500',
      kpiConfig: {
          selectorLabel: 'Advice Workflow',
          dueMonthLabel: 'Review Month',
          progressTitle: 'Advice Progress',
          staffLoadTitle: 'Advice Staff Load',
          stageLabels: {
              not_started: 'Scope Pending',
              in_progress: 'Advice In Progress',
              completed: 'Delivered / Done',
              overdue: 'Overdue Review',
          },
      },
  }),
  INDEPENDENT_EXAM: createRelationalServiceModule({
      id: 'independent_exam',
      label: 'Independent Examination',
      icon: Heart,
      colorClass: 'bg-emerald-800',
      tableName: 'clients_independent_exam',
      fetchTable: 'v_independent_exam_clients',
      enrollServiceField: 'svc_independent_exam',
    sourceTypes: ['charity', 'unregistered'],
    workflowTable: 'status_independent_exam',
      workflowKey: 'iexam',
      workflowLabel: 'Independent Examination',
      shortLabel: 'I-EXAM',
      dueLabel: 'Due (Exam)',
      textColorClass: 'text-emerald-700',
      filterColorClass: 'bg-emerald-50 border-emerald-200 text-emerald-700',
      dotColorClass: 'bg-emerald-500',
      kpiConfig: {
          selectorLabel: 'Exam Workflow',
          dueMonthLabel: 'Exam Month',
          progressTitle: 'Exam Progress',
          staffLoadTitle: 'Exam Staff Load',
          stageLabels: {
              not_started: 'Records Pending',
              in_progress: 'In Examination',
              completed: 'Report Signed',
              overdue: 'Overdue Exam',
          },
      },
  }),
  OTHER: {
      id: 'other',
      label: 'Other',
      icon: FolderOpen,
      colorClass: 'bg-slate-600',
      tableName: 'clients_other',
      columns: []
  },
};